/**
 * Redux Sagas — handles side effects for the game.
 *
 * Responsibilities:
 * - Loading the dictionary
 * - Starting classic / custom games
 * - Decoding game URL on load
 * - Submitting guesses (validation + evaluation)
 * - Saving game state to localStorage
 */
import { put, select, takeLatest, delay, all } from 'redux-saga/effects';
import {
  initGame,
  recordGuess,
  setGameResult,
  setMessage,
  clearMessage,
  setShakeRow,
  setDictionaryLoaded,
  startClassicGame,
  startCustomGame,
  createCustomGame,
  submitGuess,
  setGameCode,
} from '../slices/gameSlice';
import {
  loadDictionary,
  loadDictionarySuccess,
  loadDictionaryFailure,
} from '../slices/dictionarySlice';
import { updateLetterStates, resetKeyboard } from '../slices/keyboardSlice';
import { showResultModal } from '../slices/appSlice';
import { WORD_LIST } from '../../data/fiveLetterTurkishWords';
import { evaluateGuess, isCorrectGuess, toTurkishUpper } from '../../utils/gameLogic';
import { encodeWordToGameCode, decodeGameCodeToWord } from '../../utils/encoding';
import { saveGameState, loadGameState, clearGameState } from '../../utils/storage';
import {
  GAME_STATUS,
  GAME_MODE,
  MAX_GUESSES,
  WORD_LENGTH,
  LS_KEYS,
} from '../../utils/constants';

/**
 * Saga: Load the Turkish dictionary.
 */
function* loadDictionarySaga() {
  try {
    // Simulate async load (dictionary is a static import)
    yield delay(100);
    yield put(loadDictionarySuccess(WORD_LIST));
    yield put(setDictionaryLoaded(true));
  } catch (error) {
    yield put(loadDictionaryFailure(error.message));
  }
}

/**
 * Pick a deterministic daily word by date or random per session.
 */
function getClassicWord(words, isRandom = false) {
  if (isRandom) {
    const index = Math.floor(Math.random() * words.length);
    return words[index];
  }

  // Use date-based selection for daily word
  const today = new Date();
  const dateStr = `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`;
  let hash = 0;
  for (let i = 0; i < dateStr.length; i++) {
    hash = (hash * 31 + dateStr.charCodeAt(i)) | 0;
  }
  const index = Math.abs(hash) % words.length;
  return words[index];
}

/**
 * Saga: Start a classic game.
 */
function* startClassicGameSaga(action) {
  const isRandom = action.payload?.isRandom || false;
  const dictionary = yield select((state) => state.dictionary.words);
  const currentStatus = yield select((state) => state.game.status);
  const currentMode = yield select((state) => state.game.mode);

  // If already playing classic mode and not forcing random, do nothing
  if (!isRandom && currentStatus === GAME_STATUS.PLAYING && currentMode === GAME_MODE.CLASSIC) {
    return;
  }

  // For random games, we always start fresh
  if (isRandom) {
    clearGameState(LS_KEYS.CLASSIC_STATE);
  } else {
    // Check for saved state (only for daily word)
    const saved = loadGameState(LS_KEYS.CLASSIC_STATE);
    if (saved) {
      // Restore saved game
      yield put(initGame({
        targetWord: saved.targetWord,
        mode: GAME_MODE.CLASSIC,
        gameCode: '',
      }));
      // Replay guesses
      for (let i = 0; i < saved.guesses.length; i++) {
        yield put(recordGuess({ guess: saved.guesses[i], evaluation: saved.evaluations[i] }));
        yield put(updateLetterStates({ guess: saved.guesses[i], evaluation: saved.evaluations[i] }));
      }
      // Restore status if game was finished
      if (saved.status === GAME_STATUS.WON || saved.status === GAME_STATUS.LOST) {
        yield put(setGameResult(saved.status));
        yield put(showResultModal());
      }
      return;
    }
  }

  // Pick word
  const word = getClassicWord(dictionary, isRandom);
  yield put(resetKeyboard());
  yield put(initGame({
    targetWord: word,
    mode: GAME_MODE.CLASSIC,
    gameCode: '',
  }));

  // Save initial state so refresh doesn't lose the word (especially for random games)
  saveGameState(LS_KEYS.CLASSIC_STATE, {
    targetWord: word,
    guesses: [],
    evaluations: [],
    currentRow: 0,
    status: GAME_STATUS.PLAYING,
    mode: GAME_MODE.CLASSIC,
    gameCode: '',
  });
}

/**
 * Saga: Start a custom game (user opened a shared link).
 */
function* startCustomGameSaga(action) {
  const { gameCode } = action.payload;
  const currentStatus = yield select((state) => state.game.status);
  const currentMode = yield select((state) => state.game.mode);
  const currentGameCode = yield select((state) => state.game.gameCode);

  // If already playing this exact custom game, do nothing
  if (currentStatus === GAME_STATUS.PLAYING && currentMode === GAME_MODE.CUSTOM && currentGameCode === gameCode) {
    return;
  }

  const decoded = decodeGameCodeToWord(gameCode);

  if (!decoded) {
    yield put(setMessage('Geçersiz oyun bağlantısı.'));
    return;
  }

  const word = toTurkishUpper(decoded);

  // Check saved state for this custom game
  const saved = loadGameState(LS_KEYS.CUSTOM_STATE);
  if (saved && saved.gameCode === gameCode) {
    yield put(initGame({
      targetWord: word,
      mode: GAME_MODE.CUSTOM,
      gameCode,
    }));
    for (let i = 0; i < saved.guesses.length; i++) {
      yield put(recordGuess({ guess: saved.guesses[i], evaluation: saved.evaluations[i] }));
      yield put(updateLetterStates({ guess: saved.guesses[i], evaluation: saved.evaluations[i] }));
    }
    // Restore status if game was finished
    if (saved.status === GAME_STATUS.WON || saved.status === GAME_STATUS.LOST) {
      yield put(setGameResult(saved.status));
      yield put(showResultModal());
    }
    return;
  }

  yield put(resetKeyboard());
  yield put(initGame({
    targetWord: word,
    mode: GAME_MODE.CUSTOM,
    gameCode,
  }));

  // Save initial state for custom game
  saveGameState(LS_KEYS.CUSTOM_STATE, {
    targetWord: word,
    guesses: [],
    evaluations: [],
    currentRow: 0,
    status: GAME_STATUS.PLAYING,
    mode: GAME_MODE.CUSTOM,
    gameCode,
  });
}

/**
 * Saga: Create a custom game (generate game code).
 */
function* createCustomGameSaga(action) {
  const { word } = action.payload;
  const upper = toTurkishUpper(word);
  const dictionary = yield select((state) => state.dictionary.words);

  if ([...upper].length !== WORD_LENGTH) {
    yield put(setMessage('Kelime 5 harf olmalıdır.'));
    return;
  }

  if (!dictionary.includes(upper)) {
    yield put(setMessage('Kelime sözlükte bulunamadı.'));
    return;
  }

  const code = encodeWordToGameCode(upper);
  yield put(setGameCode(code));
}

/**
 * Saga: Submit a guess — validate and evaluate.
 */
function* submitGuessSaga() {
  const game = yield select((state) => state.game);
  const dictionary = yield select((state) => state.dictionary.words);

  const { currentGuess, targetWord, status, currentRow } = game;

  // Guard: game must be in playing state
  if (status !== GAME_STATUS.PLAYING) return;

  const guess = toTurkishUpper(currentGuess);

  // Validate length
  if ([...guess].length !== WORD_LENGTH) {
    yield put(setShakeRow(true));
    yield put(setMessage('Yeterli harf yok.'));
    yield delay(1500);
    yield put(clearMessage());
    yield put(setShakeRow(false));
    return;
  }

  // Validate against dictionary
  if (!dictionary.includes(guess)) {
    yield put(setShakeRow(true));
    yield put(setMessage('Kelime listesinde yok.'));
    yield delay(1500);
    yield put(clearMessage());
    yield put(setShakeRow(false));
    return;
  }

  // Evaluate the guess
  const evaluation = evaluateGuess(guess, targetWord);

  // Record the guess
  yield put(recordGuess({ guess, evaluation }));
  yield put(updateLetterStates({ guess, evaluation }));

  // Check win
  if (isCorrectGuess(evaluation)) {
    yield delay(1800); // wait for flip animation
    yield put(setGameResult(GAME_STATUS.WON));
    yield put(showResultModal());
  }
  // Check loss (used all guesses)
  else if (currentRow + 1 >= MAX_GUESSES) {
    yield delay(1800);
    yield put(setGameResult(GAME_STATUS.LOST));
    yield put(setMessage(`Cevap: ${targetWord}`));
    yield put(showResultModal());
  }

  // Save state for resume
  const updatedGame = yield select((state) => state.game);
  const lsKey = updatedGame.mode === GAME_MODE.CLASSIC ? LS_KEYS.CLASSIC_STATE : LS_KEYS.CUSTOM_STATE;
  saveGameState(lsKey, {
    targetWord: updatedGame.targetWord,
    guesses: updatedGame.guesses,
    evaluations: updatedGame.evaluations,
    currentRow: updatedGame.currentRow,
    status: updatedGame.status,
    mode: updatedGame.mode,
    gameCode: updatedGame.gameCode,
  });
}

/**
 * Root saga — watches for dispatched actions.
 */
export default function* rootSaga() {
  yield all([
    takeLatest(loadDictionary.type, loadDictionarySaga),
    takeLatest(startClassicGame.type, startClassicGameSaga),
    takeLatest(startCustomGame.type, startCustomGameSaga),
    takeLatest(createCustomGame.type, createCustomGameSaga),
    takeLatest(submitGuess.type, submitGuessSaga),
  ]);
}
